import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaPencilAlt, FaTrashAlt } from "react-icons/fa";
import BeatLoader from "react-spinners/BeatLoader";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { useCallbackPrompt } from "../../../../../hooks/useCallbackPrompt";
import DialogBox from "../../../../../components/DialogBox";
import { BiSolidReport } from "react-icons/bi";
import {
  createTheme,
  ThemeProvider,
  alpha,
  styled,
} from "@mui/material/styles";
import { DataGridPro, gridClasses, GridToolbar } from "@mui/x-data-grid-pro";

import "../../StudySetup.css";
import { Col, Row } from "react-bootstrap";
import { GetReportFieldValueServices } from "../../../../../services/reports";
import moment from "moment";

const getMuiTheme = createTheme({
  typography: {
    fontSize: 25,
  },
});

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity
        ),
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}));

const AllReports = () => {
  const navigate = useNavigate();
  const [load, setLoad] = useState(false);
  const [pageSize, setPageSize] = useState(5);
  const [showDialog, setShowDialog] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showDialog);
  const location = useLocation();
  const reportId = location.state.reportId;
  const reportTitle = location.state.reportTitle;
  const procName = location.state.procName;
  // Sample Report Data
  const [drugCriteriaData, setDrugCriteriaData] = useState([
    { id: 1, description: "Report 1" },
    { id: 2, description: "Report 2" },
    { id: 3, description: "Report 3" },
  ]);

  const [allAttributesData, setAllAttributesData] = useState([
    {
      id: 1,
      country: "Item A",
      SiteDepotNumber: "Error A",
      Investigator: 5,
      BlindedLotNumber: 10,
      Expiry: 50,
      AvailableATDepot: 1,
      reportId: 1,
    },
    {
      id: 2,
      country: "Item B",
      SiteDepotNumber: "Error B",
      Investigator: 3,
      BlindedLotNumber: 15,
      Expiry: 100,
      AvailableATDepot: 2,
      reportId: 2,
    },
  ]);

  const [selectedReport, setSelectedReport] = useState(
    drugCriteriaData[0]?.id || null
  );
  const [attributesData, setAttributesData] = useState([]);
  const [fieldsValues, setFieldsValues] = React.useState([]);

  useEffect(() => {
    if (selectedReport) {
      const filteredData = allAttributesData.filter(
        (item) => item.reportId === selectedReport
      );
      setAttributesData(filteredData);
    }
  }, [selectedReport, allAttributesData]);

  React.useEffect(() => {
    fetchDataForFieldBind();
  }, []);

  const fetchDataForFieldBind = async () => {
    try {
      setLoad(true);
      const res = await GetReportFieldValueServices(procName);

      if (res.status) {
        setLoad(false);
        setFieldsValues(res.data.result);
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err.message);
    }
  };

  const rows = fieldsValues?.map((row) => ({ id: row.Id, ...row }));

  const columns = Object.keys(fieldsValues[0] || {})
    .filter(
      (field) =>
        field !== "Id" &&
        field !== "id" &&
        field !== "Country" &&
        field !== "DrugType" &&
        field !== "ReportId"
    )
    .map((field) => ({
      field,
      headerName: field.replace(/([A-Z])/g, " $1").trim(),
      width: 200,
      renderHeader: () => <div className="grid-heading">{field}</div>,
      renderCell: (params) => (
        <div className="grid-body">
          <p>
            {field === "Expiry"
              ? moment(params.row[field]).format("DD-MM-YYYY")
              : typeof params.row[field] === "object"
              ? JSON.stringify(params.row[field])
              : params.row[field]}
          </p>
        </div>
      ),
    }));

  columns.push({
    field: "action",
    headerName: "Action",
    width: 100,
    renderHeader: () => <div className="grid-heading">{"Action"}</div>,
    renderCell: (params) => (
      <>
        <button
          style={{
            cursor: "pointer",
            textAlign: "center",
            backgroundColor: "#337ab7",
            padding: "8px",
            border: "none",
            borderRadius: "5px",
            marginLeft: "10px",
          }}
          onClick={() =>
            navigate(`/study-management/study-settings/edit-report`, {
              state: {
                editid: params.row.Id,
                reportId: reportId,
                reportTitle: reportTitle,
                procName: procName,
              },
            })
          }
        >
          <FaPencilAlt color="white" size={15} />
        </button>
        {/* <button
          onClick={() => {
            setAttributesData((prev) =>
              prev.filter((item) => item.id !== params.row.id)
            );
          }}
          style={{
            cursor: "pointer",
            textAlign: "center",
            backgroundColor: "red",
            padding: "8px",
            border: "none",
            borderRadius: "5px",
            marginLeft: "10px",
          }}
        >
          <FaTrashAlt color="white" style={{ fontSize: "15px" }} />
        </button> */}
      </>
    ),
  });

  return (
    <>
      {load ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
            }}
          >
            <BeatLoader color="#3661eb" />
          </div>
        </>
      ) : (
        <div className="content-body">
          <p className="study-management-link" style={{ fontWeight: "600" }}>
            <Link to="/study-management">Manage Your Study</Link> |
            <Link to="/study-management/study-settings/reports">
              Report Layout
            </Link>
            |{" "}
            <Link to="/study-management/study-settings/selected-report">
              Selected Reports
            </Link>
            |{" "}
            <p className="study-edit-link">
              {reportTitle ? reportTitle : "Selected procedure Report"}
            </p>
          </p>
          <Row style={{ marginTop: "2%", marginBottom: "2%" }}>
            <Col md={6}>
              <div className="study-management-head-start">
                <p className="study-management-heading">
                  {reportTitle ? reportTitle : "Selected procedure Report"}
                </p>
              </div>
            </Col>
          </Row>
          <Box component="form" sx={{ height: "auto", width: "100%" }}>
            <div className="flex gap-6">
              {/* <div className="w-1/4 border border-gray-300 rounded-md bg-white">
                <div className="bg-gray-200 p-1">
                  <h4 className="flex ml-1 font-[600]">
                    <BiSolidReport /> REPORTS
                  </h4>
                </div>
                <div className="border border-gray-300 rounded-md bg-white m-4">
                  {drugCriteriaData.map((item) => (
                    <div
                      key={item.id}
                      className={`border-b px-5 py-3 cursor-pointer ${
                        selectedReport === item.id ? "bg-blue-100 font-[600]" : ""
                      }`}
                      onClick={() => setSelectedReport(item.id)}
                    >
                      <p className="m-0">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div> */}

              <div className="w-full rounded-md bg-white">
                <Box sx={{ height: 400, width: "100%", display: "flex" }}>
                  <ThemeProvider theme={getMuiTheme}>
                    <StripedDataGrid
                      className="allQuestionnaire-grid"
                      rows={rows}
                      columns={columns}
                      pageSize={pageSize}
                      onPageSizeChange={(newPageSize) =>
                        setPageSize(newPageSize)
                      }
                      rowsPerPageOptions={[5, 10, 20]}
                      components={{ Toolbar: GridToolbar }}
                      pagination
                      rowHeight={38}
                      disableSelectionOnClick
                      getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0
                          ? "even"
                          : "odd"
                      }
                    />
                  </ThemeProvider>
                </Box>
                <Row style={{ marginTop: "2%" }}>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <Link to="/study-management/study-settings/selected-report">
                      <div className="study-management-head-end">
                        <button className="study-management-create-btn-lg">
                          Back To Selected Report
                        </button>
                      </div>
                    </Link>
                  </Col>
                </Row>
              </div>
            </div>
          </Box>
        </div>
      )}
    </>
  );
};

export default AllReports;
